import { LoaderCircle, Mic, Square } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { useVoiceInput } from './use-voice-input';

/** What the status shows of a recording, wherever the recording itself runs. */
type VoiceStatusModel = Pick<ReturnType<typeof useVoiceInput>, 'status' | 'cancel' | 'stop'>;

export function ComposerVoiceStatus({ voice }: { voice: VoiceStatusModel }) {
  const recording = voice.status === 'recording';
  const actionRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // The toolbar microphone is replaced by these controls; keep keyboard focus usable.
    void voice.status;
    actionRef.current?.focus();
  }, [voice.status]);
  const title = recording
    ? 'Listening'
    : voice.status === 'starting'
      ? 'Opening microphone'
      : 'Transcribing';
  const description =
    voice.status === 'starting' ? 'Allow microphone access if your browser asks.' : null;

  return (
    <div className="px-3 pb-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex shrink-0 items-center justify-center">
          {recording ? (
            <Mic className="size-4 text-destructive" aria-hidden="true" />
          ) : (
            <LoaderCircle
              className="size-4 motion-safe:animate-spin text-muted-foreground"
              aria-hidden="true"
            />
          )}
        </div>
        <span role="status" className="flex-1 whitespace-nowrap text-xs text-muted-foreground">
          {title}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            ref={recording ? undefined : actionRef}
            onClick={voice.cancel}
            className="h-control-sm rounded-md px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Cancel
          </button>
          {recording && (
            <button
              type="button"
              ref={actionRef}
              onClick={voice.stop}
              className="flex h-control-sm items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Square className="size-3 fill-current" aria-hidden="true" />
              Stop & transcribe
            </button>
          )}
        </div>
      </div>
      {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}
